import { useCallback, useMemo, useRef } from 'react';

import FormFieldId from '../../../types/FormFieldId';
import FormFieldType from '../../../types/FormFieldType';
import globalTranslations from '../../../i18n/globalTranslations';
import { normalizeBooleanField } from '../../../util/checkoutFormUtils';
import {
  draftCountryToOrderValue,
  validateManualDeliveryDraft,
} from './manualDeliveryDraftValidation';

function buildDeliveryAddressPayloadFromOrder(order) {
  const rawVal = (fieldId) => order.getCustomerField(fieldId).value;

  const str = (fieldId) => {
    const v = rawVal(fieldId);
    if (v == null) return '';
    return String(v);
  };

  const countryRaw = rawVal(FormFieldId.DELIVERY_COUNTRY);
  let countryId;
  if (
    countryRaw != null &&
    typeof countryRaw === 'object' &&
    countryRaw.id != null
  ) {
    countryId = Number(countryRaw.id);
  } else if (typeof countryRaw === 'number') {
    countryId = countryRaw;
  } else if (typeof countryRaw === 'string' && countryRaw !== '') {
    const n = Number(countryRaw);
    if (!Number.isNaN(n)) {
      countryId = n;
    }
  }

  return {
    company: str(FormFieldId.DELIVERY_COMPANY),
    firstname: str(FormFieldId.DELIVERY_FIRST_NAME),
    lastname: str(FormFieldId.DELIVERY_LAST_NAME),
    streetAddress: str(FormFieldId.DELIVERY_STREET_ADDRESS),
    postalCode: str(FormFieldId.DELIVERY_POSTAL_CODE),
    city: str(FormFieldId.DELIVERY_CITY),
    countryId,
    telephone: str(FormFieldId.DELIVERY_PHONE),
  };
}

/**
 * When `DELIVERY_IS_COMPANY` was false, `order` may omit `DELIVERY_COMPANY` from
 * `customer_fields` until the next checkout round-trip — `buildDeliveryAddressPayloadFromOrder`
 * would then send an empty `company` to address-book and skip applying company on one-time save.
 * Overlay draft values so address-book POST gets `company` and one-time `applyPayloadToOrderDeliveryFields`
 * maps `company` → DELIVERY_COMPANY when that node exists.
 */
function mergeManualDraftIntoDeliveryPayload(basePayload, draft) {
  if (!draft) return basePayload;
  const countryId =
    draft.country == null || draft.country === ''
      ? basePayload.countryId
      : draftCountryToOrderValue(draft.country);

  return {
    ...basePayload,
    company:
      draft.company == null ? basePayload.company : String(draft.company),
    firstname:
      draft.firstname == null ? basePayload.firstname : String(draft.firstname),
    lastname:
      draft.lastname == null ? basePayload.lastname : String(draft.lastname),
    streetAddress:
      draft.streetAddress == null
        ? basePayload.streetAddress
        : String(draft.streetAddress),
    postalCode:
      draft.postalCode == null
        ? basePayload.postalCode
        : String(draft.postalCode),
    city: draft.city == null ? basePayload.city : String(draft.city),
    countryId,
    telephone:
      draft.telephone == null ? basePayload.telephone : String(draft.telephone),
  };
}

/**
 * Copy MobX delivery form field values onto `order.customer_fields` before reading the order for API save.
 * Avoids empty `company` / lines when the form was edited but the order was not updated (e.g. no blur).
 */
/**
 * After `order` delivery customer_fields were updated, mirror values onto the MobX delivery form
 * so `syncDeliveryFormToOrder` and the rest of checkout stay consistent.
 */
function pushOrderDeliveryCustomerFieldsToMobXForms(order, forms) {
  const deliveryForm = forms?.deliveryForm;
  if (!deliveryForm?.fieldsArray?.length) return;
  deliveryForm.fieldsArray.forEach((formField) => {
    const id = formField.id;
    if (id === FormFieldId.DELIVERY_ADDRESS_DROPDOWN) return;
    const customerFieldNode = order.customer_fields?.get(id);
    if (!customerFieldNode) return;

    const raw = customerFieldNode.value;

    if (id === FormFieldId.DELIVERY_COUNTRY) {
      let normalized = '';
      if (raw !== null && raw !== undefined && raw !== '') {
        if (typeof raw === 'object' && raw != null && raw.id != null) {
          normalized = String(raw.id);
        } else {
          normalized = String(raw);
        }
      }
      formField.setValue(normalized);
      return;
    }
    if (formField.type === FormFieldType.BOOLEAN) {
      formField.setValue(Boolean(raw));
      return;
    }
    if (raw === undefined || raw === null || raw === '') {
      formField.setValue('');
    } else if (typeof raw === 'string' || typeof raw === 'number') {
      formField.setValue(raw);
    } else {
      formField.setValue(String(raw));
    }
  });
}

function applyManualDeliveryDraftToOrder(order, draft, applyPayload) {
  const dic = order.customer_fields?.get(FormFieldId.DELIVERY_IS_COMPANY);
  if (dic) {
    dic.setValue(Boolean(draft.deliveryIsCompany));
  }
  if (draft.deliveryIsCompany && order.ensureDeliveryCompanyCustomerField) {
    order.ensureDeliveryCompanyCustomerField();
  }

  const countryVal =
    draft.country == null || draft.country === ''
      ? null
      : draftCountryToOrderValue(draft.country);

  applyPayload({
    company: draft.company,
    firstname: draft.firstname,
    lastname: draft.lastname,
    streetAddress: draft.streetAddress,
    postalCode: draft.postalCode,
    city: draft.city,
    countryId: countryVal,
    telephone: draft.telephone,
  });

  const care = order.customer_fields?.get(FormFieldId.DELIVERY_CARE_OF);
  if (care) {
    care.setValue(
      draft.careOf === '' || draft.careOf == null ? null : draft.careOf
    );
  }
}

function syncDeliveryFormToOrder(order, deliveryForm) {
  if (!deliveryForm?.fieldsArray?.length) return;
  deliveryForm.fieldsArray.forEach((formField) => {
    const id = formField.id;
    if (id === FormFieldId.DELIVERY_ADDRESS_DROPDOWN) return;
    const customerField = order.customer_fields?.get(id);
    if (!customerField) return;

    const raw = formField.value;
    if (id === FormFieldId.DELIVERY_COUNTRY) {
      if (raw === '' || raw == null) {
        customerField.setValue(null);
      } else {
        const asNum = Number(raw);
        customerField.setValue(Number.isNaN(asNum) ? String(raw) : asNum);
      }
      return;
    }
    if (formField.type === FormFieldType.BOOLEAN) {
      customerField.setValue(Boolean(raw));
      return;
    }
    if (raw === '' || raw == null || raw === undefined) {
      customerField.setValue(null);
    } else {
      customerField.setValue(raw);
    }
  });
}

/**
 * Shared delivery address-book dropdown options, handlers, and save logic.
 * @param {object} params
 * @param {import('mobx-state-tree').Instance} params.order
 * @param {object} params.checkoutStore
 * @param {object} params.addressStore
 * @param {function} params.onOrderUpdate
 * @param {object} params.intl react-intl
 * @param {object} params.forms from useRecipientCheckoutForms
 * @param {function} [params.onAfterDropdownChange] e.g. close modal
 */
export function useDeliveryAddressBookActions({
  order,
  checkoutStore,
  addressStore,
  onOrderUpdate,
  intl,
  forms,
  onAfterDropdownChange,
}) {
  const lastCreatedAddressRef = useRef(null);
  const isB2B = order.isB2BCheckout;

  const resetDeliveryMobXFormsFromOrder = useCallback(() => {
    pushOrderDeliveryCustomerFieldsToMobXForms(order, forms);
  }, [order, forms]);

  const deliveryAddressDropdownField = order.getCustomerField(
    FormFieldId.DELIVERY_ADDRESS_DROPDOWN
  ).field;

  const deliveryAddressDropdownFormField = useMemo(() => {
    if (!deliveryAddressDropdownField) return null;
    if (isB2B) {
      return (
        forms.deliveryDropdownForm?.getField(
          FormFieldId.DELIVERY_ADDRESS_DROPDOWN
        ) ?? null
      );
    }
    return (
      forms.deliveryForm?.getField(FormFieldId.DELIVERY_ADDRESS_DROPDOWN) ??
      null
    );
  }, [
    deliveryAddressDropdownField,
    forms.deliveryForm,
    forms.deliveryDropdownForm,
    isB2B,
  ]);

  const deliveryDropdownOptions = useMemo(() => {
    if (!deliveryAddressDropdownField) return [];

    const chooseLabel = intl.formatMessage(
      globalTranslations.chooseDeliveryAddress
    );
    const placeholder = [{ value: '', label: chooseLabel }];

    const apiOptions = (deliveryAddressDropdownField.options || []).map(
      (option) => ({
        value: String(option.value),
        label: option.label,
      })
    );

    return [...placeholder, ...apiOptions];
    // `options` is often mutated in place on MST; array reference may be unchanged after checkout
    // refresh, so depend on a fingerprint of the list (new address after save must re-map).
  }, [
    deliveryAddressDropdownField,
    intl,
    (deliveryAddressDropdownField?.options || [])
      .map((o) => `${o.value}:${o.label}`)
      .join('|'),
  ]);

  const applyPayloadToOrderDeliveryFields = useCallback(
    (payload) => {
      const fieldMap = [
        [FormFieldId.DELIVERY_COMPANY, payload.company],
        [FormFieldId.DELIVERY_FIRST_NAME, payload.firstname],
        [FormFieldId.DELIVERY_LAST_NAME, payload.lastname],
        [FormFieldId.DELIVERY_STREET_ADDRESS, payload.streetAddress],
        [FormFieldId.DELIVERY_POSTAL_CODE, payload.postalCode],
        [FormFieldId.DELIVERY_CITY, payload.city],
        [FormFieldId.DELIVERY_COUNTRY, payload.countryId],
        [FormFieldId.DELIVERY_PHONE, payload.telephone],
      ];
      fieldMap.forEach(([fieldId, value]) => {
        const customerField = order.customer_fields?.get(fieldId);
        if (customerField != null && value !== undefined) {
          customerField.setValue(value === '' ? null : value);
        }
      });
    },
    [order]
  );

  const handleDeliveryAddressDropdownChange = useCallback(
    (e) => {
      if (!deliveryAddressDropdownField) return;
      checkoutStore.cancelOrder();
      const value = e.target.value;
      const fieldValue = value === '' ? null : Number(value);
      deliveryAddressDropdownField.setValue(fieldValue);
      if (deliveryAddressDropdownFormField) {
        deliveryAddressDropdownFormField.setValue(value);
      }
      const oneTimeField = order.customer_fields?.get(
        FormFieldId.ONE_TIME_DELIVERY_ADDRESS
      );
      if (oneTimeField) {
        oneTimeField.setValue(false);
      }
      onOrderUpdate({ withoutTimer: true });
      if (typeof onAfterDropdownChange === 'function') {
        onAfterDropdownChange();
      }
    },
    [
      checkoutStore,
      deliveryAddressDropdownField,
      deliveryAddressDropdownFormField,
      order,
      onOrderUpdate,
      onAfterDropdownChange,
    ]
  );

  /**
   * Apply address-book dropdown from the MobX form field to the order and refresh checkout.
   * Used when the recipient modal saves on the Address book tab (dropdown is draft until Save).
   * @returns {boolean} false if the placeholder ("choose") is still selected — caller should show validation.
   */
  const commitDeliveryAddressDropdownSelection = useCallback(() => {
    if (!deliveryAddressDropdownField || !deliveryAddressDropdownFormField) {
      return false;
    }
    checkoutStore.cancelOrder();
    const value = deliveryAddressDropdownFormField.value;
    const strVal = value === '' || value == null ? '' : String(value);
    if (strVal === '') {
      return false;
    }
    const fieldValue = Number(strVal);
    deliveryAddressDropdownField.setValue(fieldValue);
    deliveryAddressDropdownFormField.setValue(strVal);
    const oneTimeField = order.customer_fields?.get(
      FormFieldId.ONE_TIME_DELIVERY_ADDRESS
    );
    if (oneTimeField) {
      oneTimeField.setValue(false);
    }
    onOrderUpdate({ withoutTimer: true });
    if (typeof onAfterDropdownChange === 'function') {
      onAfterDropdownChange();
    }
    return true;
  }, [
    checkoutStore,
    deliveryAddressDropdownField,
    deliveryAddressDropdownFormField,
    order,
    onOrderUpdate,
    onAfterDropdownChange,
  ]);

  /**
   * Before saving the delivery form from the modal: clear book dropdown on the order (manual entry).
   * Does not set ONE_TIME — the "save to address book" switch must stay in effect for confirm/save path.
   */
  const prepareManualDeliveryAddressForSave = useCallback(() => {
    checkoutStore.cancelOrder();
    const deliveryDropdownField = order.customer_fields?.get(
      FormFieldId.DELIVERY_ADDRESS_DROPDOWN
    );
    if (deliveryDropdownField) {
      deliveryDropdownField.setValue(null);
    }
    if (deliveryAddressDropdownFormField) {
      deliveryAddressDropdownFormField.setValue('');
    }
    onOrderUpdate({ withoutTimer: true });
  }, [checkoutStore, order, deliveryAddressDropdownFormField, onOrderUpdate]);

  const handleAddressBookSave = useCallback(
    (payload) => {
      const { saveToAddressBook = true, ...addressPayload } = payload;

      if (saveToAddressBook) {
        if (!addressStore) {
          return Promise.reject();
        }
        return addressStore
          .saveAddress(addressPayload)
          .then((createdAddress) => {
            const newId = createdAddress?.id;
            if (newId != null) {
              lastCreatedAddressRef.current = createdAddress;
              const deliveryDropdownField = order.customer_fields?.get(
                FormFieldId.DELIVERY_ADDRESS_DROPDOWN
              );
              if (deliveryDropdownField) {
                const numericId =
                  typeof newId === 'number' ? newId : Number(newId);
                deliveryDropdownField.setValue(numericId);
              }
            }
            onOrderUpdate({ withoutTimer: true });
          });
      }

      applyPayloadToOrderDeliveryFields(addressPayload);
      const deliveryDropdownField = order.customer_fields?.get(
        FormFieldId.DELIVERY_ADDRESS_DROPDOWN
      );
      if (deliveryDropdownField) {
        deliveryDropdownField.setValue(null);
      }
      const oneTimeField = order.customer_fields?.get(
        FormFieldId.ONE_TIME_DELIVERY_ADDRESS
      );
      if (oneTimeField) {
        oneTimeField.setValue(true);
      }
      onOrderUpdate({ withoutTimer: true });
      return Promise.resolve();
    },
    [addressStore, order, onOrderUpdate, applyPayloadToOrderDeliveryFields]
  );

  const confirmDeliveryAddressModal = useCallback(
    (opts = {}) => {
      const {
        saveToAddressBook: saveOverride,
        manualDeliveryDraft,
        deferredSaveToAddressBookChecked,
      } = opts;

      if (deferredSaveToAddressBookChecked != null) {
        const oneTimeFieldNode = order.getCustomerField(
          FormFieldId.ONE_TIME_DELIVERY_ADDRESS
        ).field;
        if (oneTimeFieldNode) {
          oneTimeFieldNode.setValue(!deferredSaveToAddressBookChecked);
        }
      }

      if (manualDeliveryDraft != null) {
        const { valid, fieldErrors } = validateManualDeliveryDraft(
          forms.deliveryForm,
          manualDeliveryDraft,
          intl,
          order
        );
        if (!valid) {
          return Promise.reject({ fieldErrors });
        }
        applyManualDeliveryDraftToOrder(
          order,
          manualDeliveryDraft,
          applyPayloadToOrderDeliveryFields
        );
        pushOrderDeliveryCustomerFieldsToMobXForms(order, forms);
      } else {
        syncDeliveryFormToOrder(order, forms.deliveryForm);
        syncDeliveryFormToOrder(order, forms.deliveryDropdownForm);
      }
      const payload = mergeManualDraftIntoDeliveryPayload(
        buildDeliveryAddressPayloadFromOrder(order),
        manualDeliveryDraft
      );
      const oneTimeField = order.getCustomerField(
        FormFieldId.ONE_TIME_DELIVERY_ADDRESS
      ).field;
      const isOneTime = normalizeBooleanField(oneTimeField?.value);
      const saveToAddressBook =
        typeof saveOverride === 'boolean' ? saveOverride : !isOneTime;
      return handleAddressBookSave({
        ...payload,
        saveToAddressBook,
      });
    },
    [
      order,
      forms.deliveryForm,
      forms.deliveryDropdownForm,
      handleAddressBookSave,
      applyPayloadToOrderDeliveryFields,
      intl,
    ]
  );

  return {
    lastCreatedAddressRef,
    deliveryAddressDropdownField,
    deliveryAddressDropdownFormField,
    deliveryDropdownOptions,
    handleDeliveryAddressDropdownChange,
    commitDeliveryAddressDropdownSelection,
    prepareManualDeliveryAddressForSave,
    applyPayloadToOrderDeliveryFields,
    handleAddressBookSave,
    confirmDeliveryAddressModal,
    resetDeliveryMobXFormsFromOrder,
  };
}
